import type { ListPageExcelColumn } from './columns';
import { getListPageExcelColumnTitle } from './columns';

type Layout = {
  prefix: string;
  fields: string[];
  headers: string[];
};

const area = 'Business area';
const deception = ['Nature of deception', 'Nature of deception- detailed'];
const enablers = ['Fraud enabler (if applicable)', 'Fraud enabler (if multiple)'];
const end = ['COVID-19 related?', 'Instances', 'Value', 'Comments'];

const layouts: Record<string, Layout> = {
  'prevented-actual-add-instance': {
    prefix: 'prevented_actual',
    fields: [
      'area',
      'deception',
      'positions',
      'support',
      'support_additional',
      'c19',
      'impact',
      '',
      'comments',
    ],
    headers: [area, ...deception, ...enablers, ...end],
  },
  'prevented-fraud-add-instance': {
    prefix: 'prevented_future_fraud',
    fields: [
      'area',
      'deception',
      'positions',
      'support',
      'support_additional',
      'c19',
      'impact',
      '',
      'comments',
    ],
    headers: [
      area,
      ...deception,
      ...enablers,
      ...end.slice(0, -1),
      'Comments (including Prevention Bank Methodology reference number)',
    ],
  },
  'prevented-error-add-instance': {
    prefix: 'prevented_future_error',
    fields: ['area', 'responsible', 'involved', 'c19', 'impact', '', 'comments'],
    headers: [
      area,
      'Error origin',
      'Counter fraud involvement',
      ...end.slice(0, -1),
      'Comments- inc Methodology Bank reference',
    ],
  },
  'details-of-prevention': {
    prefix: 'prevented_upstream',
    fields: ['area', 'support', 'impact', '', 'comments'],
    headers: [
      area,
      'Prevention activity',
      'Instances',
      'Value',
      'Comments (including Prevention Bank Methodology reference number)',
    ],
  },
  'detected-fraud-add-instance': {
    prefix: 'detected_fraud',
    fields: [
      'area',
      'committed',
      'deception',
      'positions',
      'support',
      'support_additional',
      'c19',
      'input',
      '',
      'comments',
    ],
    headers: [area, 'Who commited the fraud?', ...deception, ...enablers, ...end],
  },
  'detected-error-add-instance': {
    prefix: 'detected_error',
    fields: ['area', 'responsible', 'c19', 'impact', '', 'comments'],
    headers: [area, 'Error origin', ...end],
  },
  'recovered-fraud-add-instance': {
    prefix: 'recovered_fraud',
    fields: [
      'area',
      'committed',
      'deception',
      'positions',
      'support',
      'c19',
      'impact',
      '',
      'comments',
    ],
    headers: [area, 'Who commited the fraud?', ...deception, 'Type of fraud recovery', ...end],
  },
  'recovered-error-add-instance': {
    prefix: 'recovered_error',
    fields: ['area', 'deception', 'recovered', 'involvement', 'c19', 'impact', '', 'comments'],
    headers: [area, 'Error origin', 'Type of error recovery', 'Counter fraud involvement', ...end],
  },
};

const normaliseHeader = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
const matchesHeaders = (row: string[], headers: string[]) =>
  row.length === headers.length &&
  row.every((cell, index) => normaliseHeader(cell) === normaliseHeader(headers[index]));

const layoutError =
  'Copy this section from the 2025-26 or 2026-27 CDR template, from Business area through Comments, with or without the header row. Keep blank spacer columns in place.';

const optionAliases: Record<string, string> = {
  'licences & permits': 'Licences and permits',
  'tax and duties': 'Tax & duties',
  'failure to declare or disclose': 'Failure to declare/disclose',
  'impersonation of purchasing cardholder': 'Impersonation of purchasing card holder',
  'risk scoring and enhanced due diligence': 'Risk scoring & Enhanced Due Diligence',
  'contingent labour': 'Contigent labour',
  offsetting: 'Offsetting loss',
  'phishing, smshing and vishing': 'Phishing, vishing and SMShing',
};

const normaliseOption = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();

const mapOptionRows = (rows: string[][], columns: ListPageExcelColumn[]) =>
  rows.map((row) =>
    row.map((value, index) => {
      const options = columns[index]?.descriptors.flatMap((descriptor) => descriptor.options) ?? [];
      const normalised = normaliseOption(value);
      const match =
        options.find(
          (option) =>
            normaliseOption(option.label) === normalised ||
            normaliseOption(option.value) === normalised,
        ) ?? options.find((option) => option.label === optionAliases[normalised]);
      return match?.value ?? value;
    }),
  );

export type PsfaClipboardResult = {
  rows: string[][];
  message?: string;
  error?: string;
};

export const mapPsfaCdrClipboard = (
  rows: string[][],
  pageName: string,
  columns: ListPageExcelColumn[],
): PsfaClipboardResult => {
  const layout = layouts[pageName];
  if (!layout || rows.length === 0) return { rows };
  const editorHeaders = columns.map(getListPageExcelColumnTitle);
  if (matchesHeaders(rows[0], editorHeaders)) {
    return { rows: mapOptionRows(rows.slice(1), columns) };
  }

  const count = layout.fields.length;
  const compactHeader = matchesHeaders(rows[0], layout.headers);
  let sourceRows: string[][];
  if (compactHeader) {
    sourceRows = rows.slice(1);
    if (sourceRows.some((row) => row.length !== count)) return { rows: [], error: layoutError };
  } else {
    const headerStart = rows[0].findIndex(
      (cell) => normaliseHeader(cell) === normaliseHeader(area),
    );
    const hasHeader = headerStart >= 0;
    const candidates = hasHeader ? [headerStart] : [0, 1, 4];
    const start = candidates.find((offset) =>
      rows.every((row, rowIndex) => {
        if (row.length < offset + count * 2 - 1 || row.length > 25) return false;
        return row.every((cell, index) => {
          const sourceIndex = (index - offset) / 2;
          if (Number.isInteger(sourceIndex) && sourceIndex >= 0 && sourceIndex < count) {
            return (
              !hasHeader ||
              rowIndex !== 0 ||
              normaliseHeader(cell) === normaliseHeader(layout.headers[sourceIndex])
            );
          }
          return (
            cell.trim() === '' ||
            (hasHeader &&
              rowIndex === 0 &&
              index === offset - 1 &&
              /^\([a-z]\)$/i.test(cell.trim()))
          );
        });
      }),
    );
    if (start !== undefined) {
      sourceRows = rows
        .slice(hasHeader ? 1 : 0)
        .map((row) => layout.fields.map((_field, index) => row[start + index * 2] ?? ''));
    } else if (!hasHeader && rows.every((row) => row.length === columns.length)) {
      return { rows: mapOptionRows(rows, columns) };
    } else if (!hasHeader && rows.every((row) => row.length === count)) {
      sourceRows = rows;
    } else {
      return { rows: [], error: layoutError };
    }
  }

  const fieldName = (field: string) => (field ? `${layout.prefix}_${field}` : layout.prefix);
  const mappedRows = sourceRows.map((row) => {
    const values = new Map(
      layout.fields.map((field, index) => [fieldName(field), row[index] ?? '']),
    );
    return columns.map((column) => values.get(String(column.descriptors[0].question.name)) ?? '');
  });
  const missing = columns.filter(
    (column) =>
      !layout.fields.some((field) => fieldName(field) === column.descriptors[0].question.name),
  );
  const notes = missing.length
    ? ` Complete fields absent from the template: ${missing.map(getListPageExcelColumnTitle).join('; ')}. Methodology references in the template remain in Comments.`
    : '';
  return {
    rows: mapOptionRows(mappedRows, columns),
    message: `Loaded CDR rows.${notes}`,
  };
};
